"use client"

import React, { useEffect, useState, useContext } from 'react';
import { Card } from '@heroui/react';
import { CountdownContext } from '@/lib/CountdownContext';

interface CountdownProps {
  targetDate: Date;
  render?: (timeLeft: TimeLeft) => React.ReactNode;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalMs: number;
}

export const CountdownTimer: React.FC<CountdownProps> = ({ targetDate, render }) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);
  const { hasFinished, setHasFinished } = useContext(CountdownContext);

  useEffect(() => {
    const update = () => {
      const newTimeLeft = calculateTimeLeft(targetDate);
      setTimeLeft(newTimeLeft);
      if (newTimeLeft.totalMs === 0) {
        setHasFinished(true);
      }
    };

    update();

    const interval = setInterval(update, 1000);

    if (hasFinished) {
      clearInterval(interval)
    }

    return () => clearInterval(interval);
  }, [targetDate]);

  if (!timeLeft) return null;

  const dateTime = [
    { time: formatNumber(timeLeft.days), text: "Days" },
    { time: formatNumber(timeLeft.hours), text: "Hours" },
    { time: formatNumber(timeLeft.minutes), text: "Minutes" },
    { time: formatNumber(timeLeft.seconds), text: "Seconds" },
  ];

  const defaultRender = (
    <div className="flex gap-2 justify-center py-5">
      {dateTime.map((item, idx) => (
        <Card key={idx} className="flex w-1/8 min-w-18 h-20 bg-primary-1000/50 backdrop-blur-2xl purple-glow p-2 text-center text-white justify-center">
          <div className="text-md lg:text-xl">
            <div>{item.time}</div>
            <div>{item.text}</div>
          </div>
        </Card>
      ))}
    </div>
  );

  return render ? render(timeLeft) : defaultRender;
};

function calculateTimeLeft(target: string | Date): TimeLeft {
  const targetMs = typeof target === 'string' ? new Date(target).getTime() : target.getTime();
  const now = Date.now();
  let diff = targetMs - now;
  if (diff < 0) diff = 0;

  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / (24 * 3600));
  const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return { days, hours, minutes, seconds, totalMs: diff };
}

function formatNumber(num: number): string {
  return num.toString().padStart(2, '0');
}
